import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useHistory } from "react-router-dom";
import { format, parseISO, isAfter } from "date-fns";
import { sv } from "date-fns/locale";
import { Card, Badge, Spinner, Alert, OverlayTrigger, Tooltip } from "react-bootstrap";
import axios from "../../../../../services/AxiosInstance";
import { getApiUrl } from "../../../../../utils/ApiUtils";
import PageTitle from "../../../../layouts/PageTitle";
import StatusTimeline from "./StatusTimelineBootstrap";
import CustomerDetailsCardBootstrap from "./CustomerDetailsCardBootstrap";
import InvoiceStatusCardBootstrap from "./InvoiceStatusCardBootstrap";
import InvoicePreviewCardBootstrap from "./InvoicePreviewCardBootstrap";
import InvoiceNotesList from "../Notes/InvoiceNotesList";
import VerificationDetailsModal from "../../../Accounting/Verification/VerificationDetailsModal";
import Swal from "sweetalert2";
import { Button, Dropdown, ButtonGroup } from "react-bootstrap";
import { toast } from "sonner";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getStatusColor, INVOICE_STATUSES } from '../../../../../config/invoiceStatus';
import { Receipt, ExternalLink, Undo2, CheckCircle as CheckCircleLucide, Info, Banknote, User as UserIcon, Clock as ClockIcon, AlertTriangle as AlertTriangleIcon, Download, Eye, File } from 'lucide-react';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import { useAccountingSettings } from '../../../../../context/AccountingSettingsContext';
import {
  faFileInvoice,
  faUser,
  faBuilding,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faCalendarAlt,
  faArrowLeft,
  faDownload,
  faBell,
  faBan,
  faPlus,
  faCreditCard,
  faEllipsisV,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faReceipt,
  faLock,
  faHistory,
  faHourglassHalf,
  faCog
} from '@fortawesome/free-solid-svg-icons';

const InvoiceDetailBootstrap = () => {
  const history = useHistory();
  const { companyId, invoiceId } = useParams();
  const { accountingSettings } = useAccountingSettings();
  const [invoice, setInvoice] = useState(null);
  const [pdfData, setPdfData] = useState(null);
  const [statusHistory, setStatusHistory] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalPaid, setTotalPaid] = useState(0);
  const [combinedTimeline, setCombinedTimeline] = useState([]);
  const invoiceRef = useRef(null);
  const [customerData, setCustomerData] = useState(null);
  const [showVerificationDetails, setShowVerificationDetails] = useState(false);
  const [selectedVerification, setSelectedVerification] = useState(null);
  const [undoLoading, setUndoLoading] = useState(false);

  // Constants for document types
  const DocumentType = {
    INVOICE: 'invoice',
    CREDIT_NOTE: 'credit_note'
  };

  // Alias for convenience (imported from config/invoiceStatus)
  const StatusType = INVOICE_STATUSES;

  // Fetch all invoice data including verifications
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        // Fetch invoice details first to get customer ID
        const invoiceResponse = await axios.get(
          getApiUrl(`${companyId}/invoices/${invoiceId}/details`),
          { withCredentials: true }
        );

        const invoiceData = invoiceResponse.data.data;
        const customerId = invoiceData.customerid;

        // Now fetch all data in parallel including customer details and verifications
        const [statusHistoryResponse, paymentsResponse, customerResponse, verificationsResponse] = await Promise.all([
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/status-history`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch status history:", error);
              return { data: { data: [] } };
            }),
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/payments`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch payments:", error);
              return { data: { data: [] } };
            }),
          customerId ?
            axios.get(getApiUrl(`${companyId}/sales/customers/${customerId}`), { withCredentials: true })
              .catch(error => {
                console.warn("Failed to fetch customer details:", error);
                return { data: null };
              })
            : Promise.resolve({ data: null }),
          // Fetch verifications for this invoice
          axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/verifications`), { withCredentials: true })
            .catch(error => {
              console.warn("Failed to fetch verifications:", error);
              return { data: { data: [] } };
            })
        ]);

        // Extract data
        const statusHistoryData = statusHistoryResponse.data.data || [];
        const paymentsData = paymentsResponse.data.data || [];
        const customerData = customerResponse?.data;
        const verificationsData = verificationsResponse.data.data || [];

        console.log("Invoice data:", invoiceData);
        console.log("Customer data:", customerData);
        console.log("Status history:", statusHistoryData);
        console.log("Payments:", paymentsData);
        console.log("Verifications:", verificationsData);

        // Set state with fetched data
        setInvoice(invoiceData);
        setStatusHistory(statusHistoryData);
        setPaymentHistory(paymentsData);
        setCustomerData(customerData);
        setVerifications(verificationsData);
        setTotalPaid(paymentsData.reduce((sum, p) => sum + parseFloat(p.amount_paid), 0));

        // Combine timeline events including verifications
        const combinedEvents = [];

        // Add status events
        statusHistoryData.forEach(status => {
          combinedEvents.push({
            date: parseISO(status.changed_at),
            type: 'status',
            status: status.status,
            change_type: status.change_type,
            displayName: status.status,
            icon: faCheckCircle
          });
        });

        // Add payment events
        paymentsData.forEach(payment => {
          combinedEvents.push({
            date: parseISO(payment.created_at || payment.payment_date),
            type: 'payment',
            amount: parseFloat(payment.amount_paid),
            verificationNumber: payment.verification_number,
            paymentMethod: payment.payment_method || 'Bank transfer',
            status: 'Payment',
            isPayment: true,
            icon: faCreditCard
          });
        });

        // Add verification events — include invoice, year-end, and annulment verifications.
        // Exclude payment/refund verifications since those are shown as payment events.
        verificationsData
          .filter(v => ['customer_invoice', 'credit_note_publication', 'customer_invoice_year_end', 'verification_reversal'].includes(v.reference_type))
          .forEach(verification => {
            combinedEvents.push({
              date: parseISO(verification.date),
              type: 'verification',
              verificationId: verification.verification_id,
              verificationNumber: `${verification.series}${verification.number}`,
              description: verification.description,
              referenceType: verification.reference_type,
              referenceStatus: verification.reference_status,
              status: 'Verification',
              isLocked: verification.is_locked,
              icon: faReceipt
            });
          });

        // Add credit note events
        if (invoiceData.credit_notes?.length > 0) {
          invoiceData.credit_notes.forEach(creditNote => {
            combinedEvents.push({
              date: parseISO(creditNote.created_at || creditNote.invoicedate || creditNote.date),
              type: 'credit_note',
              creditNoteId: creditNote.id || creditNote.credit_note_id,
              creditNoteNumber: creditNote.creditnote_number || creditNote.credit_note_number,
              amount: Math.abs(parseFloat(creditNote.total_sum || creditNote.amount || 0)) + Math.abs(parseFloat(creditNote.tax_amount || 0)),
              creditNoteStatus: creditNote.status,
              refundStatus: creditNote.refund_status,
              icon: faFileInvoice
            });
          });
        }

        // Sort by date (newest first)
        combinedEvents.sort((a, b) => b.date.getTime() - a.date.getTime());
        setCombinedTimeline(combinedEvents);

        console.log("Combined timeline:", combinedEvents);

        // Fetch PDF after everything else is loaded
        fetchPdf();
      } catch (error) {
        console.error("Error fetching invoice data:", error);
        setError("Kunde inte hämta fakturadetaljer. Vänligen försök igen senare.");
      } finally {
        setLoading(false);
      }
    }

    if (invoiceId && companyId) {
      fetchData();
    }
  }, [companyId, invoiceId]);

  const fetchPdf = async () => {
    try {
      const response = await axios.get(getApiUrl(`${companyId}/invoices/${invoiceId}/pdf`), {
        responseType: 'blob',
        withCredentials: true,
      });

      const file = new Blob([response.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      setPdfData(fileURL);
    } catch (error) {
      console.error('Error fetching the PDF:', error);
    }
  };

  // Function to view verification details in modal (matches supplier invoice pattern)
  const handleViewVerification = async (verification) => {
    try {
      const verificationId = typeof verification === 'string' ? verification : verification.verification_id;
      const response = await axios.get(
        getApiUrl(`${companyId}/verification/${verificationId}`),
        { withCredentials: true }
      );
      const data = response.data;
      if (!data || !data.verification_id) return;

      const transactions = (data.transactions || []).map(tx => ({
        transaction_id: tx.transaction_id,
        account_id: tx.account_id,
        account_number: tx.account_number,
        account_name: tx.account_name,
        account_type: tx.account_type,
        amount: tx.amount,
        transaction_type: tx.transaction_type,
        description: tx.description
      }));

      let debitSum = 0;
      let creditSum = 0;
      transactions.forEach(tx => {
        if (tx.transaction_type === 'debit') debitSum += parseFloat(tx.amount);
        else if (tx.transaction_type === 'credit') creditSum += parseFloat(tx.amount);
      });

      setSelectedVerification({
        verification_id: data.verification_id,
        date: data.date,
        verification_description: data.description,
        series: data.series,
        number: data.number,
        created_at: data.created_at,
        reference: data.reference,
        notes: data.notes,
        transactions,
        debitSum,
        creditSum
      });
      setShowVerificationDetails(true);
    } catch (error) {
      console.error('Error fetching verification details:', error);
    }
  };

  const handleRegisterPayment = () => {
    const isCreditNoteRefund = invoice.invoice_type === 'credit_note' && parseFloat(invoice.refund_amount || 0) > 0;
    const prefillAmount = isCreditNoteRefund
      ? parseFloat(invoice.refund_amount).toFixed(2)
      : remainingAmount.toFixed(2);
    const dialogTitle = isCreditNoteRefund ? 'Registrera återbetalning' : 'Registrera betalning';
    const accountLabel = isCreditNoteRefund ? 'Återbetalning skickad från konto' : 'Betalning mottagen på konto';

    Swal.fire({
      title: dialogTitle,
      html: `
      <form id="payment-form" class="text-start">
        <div class="mb-3">
          <label for="payment-amount" class="form-label">Belopp</label>
          <div class="input-group">
            <input
              type="number"
              class="form-control"
              id="payment-amount"
              value="${prefillAmount}"
              min="0"
              max="${prefillAmount}"
              step="0.01"
            />
            <span class="input-group-text">SEK</span>
          </div>
        </div>
        <div class="mb-3">
          <label for="payment-date" class="form-label">${isCreditNoteRefund ? 'Återbetalningsdatum' : 'Betalningsdatum'}</label>
          <input
            type="date"
            class="form-control"
            id="payment-date"
            value="${format(new Date(), 'yyyy-MM-dd')}"
            min="${invoice.invoicedate || ''}"
            max="${format(new Date(), 'yyyy-MM-dd')}"
          />
        </div>
        <div class="mb-3">
          <label for="payment-method" class="form-label">${accountLabel}</label>
          <select class="form-select" id="payment-method">
            <option value="1930">Bankkonto</option>
            <option value="1910">Kontant</option>
            <option value="1940">Swish</option>
          </select>
        </div>
      </form>
    `,
      showCancelButton: true,
      confirmButtonText: dialogTitle,
      cancelButtonText: 'Avbryt',
      preConfirm: () => {
        const amount = document.getElementById('payment-amount').value;
        const date = document.getElementById('payment-date').value;
        const method = document.getElementById('payment-method').value;

        if (!amount || !date) {
          Swal.showValidationMessage('Vänligen fyll i alla obligatoriska fält');
          return false;
        }

        return { amount, date, method };
      }
    }).then((result) => {
      if (result.isConfirmed) {
        const { amount, date, method } = result.value;

        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/register-payment`),
          {
            paidAmount: parseFloat(amount),
            paymentDate: date,
            selectedAccount: method,
          },
          { withCredentials: true }
        )
          .then(() => {
            // Show success toast
            toast.success(isCreditNoteRefund ? 'Återbetalning registrerad!' : 'Betalning registrerad!', {
              description: isCreditNoteRefund
                ? 'Återbetalningen har bokförts (2820 → bank).'
                : 'Fakturan har markerats som betald.',
              duration: 3000,
            });

            // Reload the page after a short delay to show the toast
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error registering payment:", error);
            toast.error('Fel!', {
              description: isCreditNoteRefund
                ? 'Kunde inte registrera återbetalningen. Försök igen senare.'
                : 'Kunde inte registrera betalningen. Försök igen senare.',
              duration: 3000,
            });
          });
      }
    });
  };

  const handleCreateCreditNote = () => {
    history.push(`/${companyId}/create-credit-note/${invoice.invoiceid || invoice.id}`);
  };

  const handleSendReminder = () => {
    Swal.fire({
      title: 'Skicka påminnelse',
      text: `Vill du skicka en betalningspåminnelse för faktura #${invoice.invoicenumber}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Ja, skicka påminnelse',
      cancelButtonText: 'Avbryt'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/send-reminder`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Påminnelse skickad!', {
              description: 'Betalningspåminnelsen har skickats till kunden.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error sending reminder:", error);
            toast.error('Fel!', {
              description: 'Kunde inte skicka påminnelsen. Försök igen senare.'
            });
          });
      }
    });
  };

  const handleCancelInvoice = () => {
    Swal.fire({
      title: 'Makulera faktura',
      text: `Är du säker på att du vill makulera faktura #${invoice.invoicenumber}? Detta kan inte ångras.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ja, makulera faktura',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/cancel`),
          {},
          { withCredentials: true }
        )
          .then(() => {
            toast.success('Fakturan makulerad!', {
              description: 'Fakturan har makulerats och kan inte längre betalas.'
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error cancelling invoice:", error);
            toast.error('Fel!', {
              description: 'Kunde inte makulera fakturan. Försök igen senare.'
            });
          });
      }
    });
  };

  // Undo info map - maps status to undo impact description
  const getUndoInfo = (status) => {
    const infoMap = {
      [INVOICE_STATUSES.PARTIALLY_PAID]: {
        title: 'Ångra senaste betalning?',
        impacts: ['Annulleringsverifikat skapas', 'Betalningen markeras som omvänd'],
        level: 'warning'
      },
      [INVOICE_STATUSES.PAID]: {
        title: 'Ångra senaste betalning?',
        impacts: ['Annulleringsverifikat skapas', 'Betalningen markeras som omvänd', 'Fakturan återställs till föregående status'],
        level: 'warning'
      }
    };
    return infoMap[status] || null;
  };

  const handleUndoClick = () => {
    const undoInfo = getUndoInfo(invoice.status);
    if (!undoInfo) return;

    Swal.fire({
      title: undoInfo.title,
      html: `
        <div class="text-start">
          <p class="mb-2"><strong>Detta kommer att:</strong></p>
          <ul class="mb-0">
            ${undoInfo.impacts.map(i => `<li>${i}</li>`).join('')}
          </ul>
        </div>
      `,
      icon: undoInfo.level,
      showCancelButton: true,
      confirmButtonText: 'Ja, ångra',
      cancelButtonText: 'Avbryt',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        setUndoLoading(true);
        axios.post(
          getApiUrl(`${companyId}/invoices/${invoiceId}/undo`),
          {},
          { withCredentials: true }
        )
          .then((response) => {
            toast.success('Steget har ångrats.', {
              description: `Status ändrad till ${response.data?.status || 'föregående status'}`,
              duration: 3000
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          })
          .catch(error => {
            console.error("Error undoing step:", error);
            const message = error.response?.data?.message || 'Kunde inte ångra steget. Försök igen senare.';
            toast.error('Fel!', { description: message, duration: 5000 });
            setUndoLoading(false);
          });
      }
    });
  };

  // Helper function for status display names (Swedish translations)
  const getStatusDisplayName = (status, documentType = 'invoice') => {
    const statusNames = {
      [DocumentType.INVOICE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.SENT]: 'Skickad',
        [StatusType.PARTIALLY_PAID]: 'Delvis betald',
        [StatusType.PAID]: 'Betald',
        [StatusType.CANCELLED]: 'Makulerad',
        [StatusType.CREDITED]: 'Krediterad',
        'Reminder Sent': 'Påminnelse skickad'
      },
      [DocumentType.CREDIT_NOTE]: {
        [StatusType.DRAFT]: 'Utkast',
        [StatusType.SENT]: 'Skickad',
        'Applied': 'Tillämpad på faktura',  // Legacy credit note status
        [StatusType.CREDITED]: 'Krediterad',
        [StatusType.CANCELLED]: 'Makulerad'
      }
    };

    return statusNames[documentType]?.[status] || status;
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };

  // Get status color and icon (using centralized config for colors)
  const getStatusConfig = (status) => {
    const colorVariant = getStatusColor(status);

    // Map Bootstrap color variants to custom colors
    const colorMap = {
      'secondary': { color: '#6b7280', bg: '#f3f4f6' },
      'primary': { color: '#3b82f6', bg: '#eff6ff' },
      'warning': { color: '#f59e0b', bg: '#fffbeb' },  // Orange for Partially_Paid
      'success': { color: '#059669', bg: '#f0fdf4' },
      'danger': { color: '#dc2626', bg: '#fef2f2' },
      'info': { color: '#8b5cf6', bg: '#f5f3ff' }  // Purple for Credited
    };

    const colors = colorMap[colorVariant] || colorMap['secondary'];

    const configs = {
      [INVOICE_STATUSES.DRAFT]: { ...colors, icon: faClock },
      [INVOICE_STATUSES.SENT]: { ...colorMap['primary'], icon: faFileInvoice },
      [INVOICE_STATUSES.PARTIALLY_PAID]: { ...colorMap['warning'], icon: faHourglassHalf },
      [INVOICE_STATUSES.PAID]: { ...colorMap['success'], icon: faCheckCircle },
      [INVOICE_STATUSES.CANCELLED]: { ...colorMap['secondary'], icon: faBan },
      [INVOICE_STATUSES.CREDITED]: { ...colorMap['info'], icon: faReceipt }
    };

    return configs[status] || { color: '#6b7280', bg: '#f3f4f6', icon: faFileInvoice };
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Laddar fakturadetaljer...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <div className="invoice-detail-container">
        <Alert variant="danger" className="text-center">
          <h5>Ett fel uppstod</h5>
          <p>{error}</p>
          <Link
            to={`/${companyId}/invoices`}
            className="btn btn-outline-danger btn-modern"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="invoice-detail-container">
        <Alert variant="warning" className="text-center">
          <h5>Faktura hittades inte</h5>
          <p>Den begärda fakturan kunde inte hittas.</p>
          <Link
            to={`/${companyId}/invoices`}
            className="btn btn-outline-warning btn-modern"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Tillbaka till fakturalistan
          </Link>
        </Alert>
      </div>
    );
  }

  // Calculate remaining amount and effective amount
  const totalCreditNoteAmount = (invoice.credit_notes || []).reduce(
    (sum, cn) => {
      const subtotal = Math.abs(parseFloat(cn.total_sum || cn.amount || 0));
      const tax = Math.abs(parseFloat(cn.tax_amount || 0));
      return sum + subtotal + tax;
    }, 0);

  const effectiveAmount = invoice.totals.grand_total - totalCreditNoteAmount;
  const remainingAmount = effectiveAmount - totalPaid;
  const statusConfig = getStatusConfig(invoice.status);

  // Calculate days until due date
  const today = new Date();
  const dueDate = parseISO(invoice.deadline);
  const daysUntilDue = Math.ceil((dueDate - today) / (1000 * 60 * 60 * 24));

  return (
    <>
      <style>{`
        .invoice-detail-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .invoice-header {
          margin-bottom: 2rem;
        }

        .back-link {
          color: var(--primary) !important;
          text-decoration: none !important;
          transition: all 0.2s ease;
          font-weight: 500;
          font-size: 0.875rem;
        }

        .back-link:hover {
          color: var(--primary-hover) !important;
          transform: translateX(-2px);
        }

        .invoice-number {
          font-size: 1.75rem;
          font-weight: 800;
          margin-bottom: 0.25rem;
          color: var(--title);
          line-height: 1;
        }

        .invoice-subtitle {
          color: var(--secondary);
          font-size: 0.875rem;
          margin-bottom: 1rem;
          font-weight: 400;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.375rem;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-weight: 600;
          font-size: 0.75rem;
          margin-bottom: 1rem;
        }

        .customer-info-header {
          background: var(--bs-light);
          border: 1px solid var(--bs-light);
          border-radius: 8px;
          padding: 1rem;
          margin-top: 1rem;
        }

        .customer-name-header {
          font-size: 1rem;
          font-weight: 600;
          color: var(--title);
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .customer-org-header {
          color: var(--secondary);
          font-size: 0.75rem;
          margin-bottom: 0.75rem;
        }

        .customer-details-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 0.75rem;
        }

        .customer-detail-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.75rem;
          color: var(--secondary);
        }

        .customer-detail-icon {
          width: 14px;
          height: 14px;
          color: var(--primary);
          flex-shrink: 0;
        }

        .info-strip {
          display: flex;
          align-items: center;
          background: white;
          border-radius: 12px;
          padding: 1.25rem 1.5rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          gap: 1.5rem;
          margin-bottom: 1.5rem;
        }
        .info-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
          min-width: 0;
        }
        .info-value {
          font-size: 0.9375rem;
          font-weight: 600;
          color: var(--title);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .info-label {
          font-size: 0.6875rem;
          color: var(--secondary);
          text-transform: uppercase;
          letter-spacing: 0.03em;
        }
        .info-divider {
          width: 1px;
          height: 2rem;
          background: #e2e8f0;
          flex-shrink: 0;
        }
        @media (max-width: 768px) {
          .info-strip {
            flex-direction: column;
            gap: 1rem;
            align-items: flex-start;
          }
          .info-divider {
            width: 100%;
            height: 1px;
          }
        }

        /* Document Viewer */
        .doc-viewer-container {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
          margin-top: 2rem;
        }
        .doc-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          background: #fafbfc;
          border-bottom: 1px solid #f1f5f9;
        }
        .doc-frame {
          height: calc(100vh - 200px);
          min-height: 600px;
          overflow: auto;
          background: #525659;
        }
        .doc-frame .rpv-core__viewer {
          height: 100%;
        }

        .main-content {
          display: grid;
          grid-template-columns: 1fr;
          gap: 2rem;
          margin-bottom: 2rem;
        }

        .content-card {
          background: white;
          border: none;
          border-radius: 8px;
          padding: 1.5rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
          transition: all 0.3s ease;
        }

        .content-card:hover {
          box-shadow: 0 6px 24px rgba(0, 0, 0, 0.1);
        }

        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1.5rem;
          padding-bottom: 0.75rem;
          border-bottom: 2px solid var(--bs-light);
        }

        .card-title {
          font-size: 1rem;
          font-weight: 700;
          color: var(--title);
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .card-icon {
          width: 18px;
          height: 18px;
          color: var(--primary);
        }

        .action-buttons {
          display: flex;
          gap: 0.75rem;
          flex-wrap: wrap;
        }

        .btn-group .btn-modern {
          border-radius: 0;
        }

        .btn-group .btn-modern:first-child {
          border-top-left-radius: 8px;
          border-bottom-left-radius: 8px;
          border-right: none;
        }

        .btn-group .btn-modern:last-child {
          border-top-right-radius: 8px;
          border-bottom-right-radius: 8px;
          border-left: 1px solid rgba(255, 255, 255, 0.2);
        }

        .btn-group .btn-modern:hover {
          transform: none;
        }

        .btn-group:hover .btn-modern {
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
        }

        .dropdown-toggle::after {
          margin-left: 0;
        }

        .btn-modern {
          border-radius: 8px;
          padding: 0.75rem 1rem;
          font-weight: 600;
          font-size: 0.8125rem;
          display: flex;
          align-items: center;
          gap: 0.375rem;
          transition: all 0.3s ease;
          border: none;
          position: relative;
          overflow: hidden;
        }

        .btn-modern::before {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.1), transparent);
          transform: translateX(-100%);
          transition: transform 0.6s ease;
        }

        .btn-modern:hover::before {
          transform: translateX(100%);
        }

        .btn-modern:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
        }

        .btn-primary-modern {
          background: var(--primary);
          color: white;
        }

        .btn-primary-modern:hover {
          background: var(--primary-hover);
          color: white;
        }

        /* Step Indicator — matches supplier invoice stepper style */
        .si-step-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          font-weight: 600;
          font-size: 0.875rem;
          transition: all 0.3s ease;
          flex-shrink: 0;
        }

        .si-step-indicator.completed {
          background: var(--bs-success);
          color: white;
        }

        .si-step-indicator.current {
          background: var(--primary);
          color: white;
          animation: si-pulse 2s infinite;
        }

        .si-step-indicator.pending {
          background: #f3f4f6;
          color: #9ca3af;
        }

        @keyframes si-pulse {
          0% { box-shadow: 0 0 0 0 var(--rgba-primary-4); }
          70% { box-shadow: 0 0 0 10px transparent; }
          100% { box-shadow: 0 0 0 0 transparent; }
        }

        .si-step-block {
          display: flex;
          align-items: center;
        }

        .si-step-label {
          font-weight: 600;
          font-size: 0.875rem;
          color: #1e293b;
          transition: color 0.2s ease;
        }

        .si-step-sublabel {
          font-size: 0.75rem;
          color: #64748b;
        }

        .si-step-block.completed .si-step-label {
          color: #166534;
        }

        .si-step-block.current .si-step-label {
          color: var(--primary);
        }

        .si-step-block.pending .si-step-label {
          color: #9ca3af;
        }

        .si-step-block.pending .si-step-sublabel {
          color: #d1d5db;
        }

        .step-connector {
          flex: 1;
          height: 2px;
          background: #e5e7eb;
          margin: 0 0.5rem;
          min-width: 20px;
        }

        .step-connector.completed {
          background: var(--bs-success);
        }

        .undo-step-link {
          display: inline-flex;
          align-items: center;
          font-size: 0.7rem;
          color: #dc3545;
          cursor: pointer;
          margin-top: 2px;
          background: none;
          border: none;
          padding: 0;
          text-decoration: none;
        }

        .undo-step-link:hover {
          text-decoration: underline;
          color: #a71d2a;
        }

        @media (max-width: 768px) {
          .si-steps-row {
            flex-direction: column !important;
            gap: 0.5rem;
          }

          .si-step-block {
            background: #fff;
            padding: 0.5rem;
            border-radius: 8px;
            border: 1px solid #e5e7eb;
          }

          .si-step-indicator {
            width: 36px;
            height: 36px;
          }

          .step-connector {
            display: none;
          }
        }

        /* Events List below stepper */
        .events-list {
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid var(--bs-light);
        }

        .events-list-title {
          font-size: 0.75rem;
          font-weight: 600;
          color: var(--secondary);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 1rem;
        }

        .event-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.75rem;
          background: var(--bs-light);
          border-radius: 8px;
          margin-bottom: 0.5rem;
          transition: all 0.2s ease;
        }

        .event-item:hover {
          background: var(--rgba-primary-1);
        }

        .event-item.clickable {
          cursor: pointer;
        }

        .event-item.clickable:hover {
          border-color: var(--primary);
        }

        .event-icon {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          flex-shrink: 0;
        }

        .event-content {
          flex: 1;
          min-width: 0;
        }

        .event-title {
          font-weight: 600;
          color: var(--title);
          font-size: 0.8125rem;
          margin-bottom: 0.125rem;
        }

        .event-date {
          color: var(--secondary);
          font-size: 0.6875rem;
        }

        .event-badge {
          font-size: 0.6875rem;
          padding: 0.25rem 0.5rem;
          border-radius: 6px;
          font-weight: 500;
        }


        @media (max-width: 1024px) {
          .invoice-header {
            padding: 1.5rem;
          }

          .invoice-number {
            font-size: 1.875rem;
          }


          .action-buttons {
            flex-direction: column;
          }

          .btn-modern {
            justify-content: center;
          }

          .content-card {
            padding: 1.25rem;
          }

          .customer-details-grid {
            grid-template-columns: 1fr;
            gap: 0.5rem;
          }
        }

        @media (max-width: 768px) {

          .invoice-number {
            font-size: 1.75rem;
          }

          .timeline-item-compact {
            flex-direction: column;
            gap: 0.5rem;
          }
        }
      `}</style>

      <PageTitle activeMenu="Fakturadetaljer" motherMenu="Fakturor" />

      <div className="invoice-detail-container">
        {/* Back Button - Top Left */}
        <div className="mb-3">
          <Button
            variant="link"
            className="p-0 text-decoration-none d-flex align-items-center gap-2"
            onClick={() => history.push(`/${companyId}/invoices`)}
            style={{ color: 'var(--secondary)', fontSize: '0.875rem' }}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="sm" />
            Tillbaka till listan
          </Button>
        </div>

        {/* Enhanced Header with Customer Info */}
        <div className="invoice-header">
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <h2 className="mb-2" style={{ fontWeight: '700' }}>Faktura {invoice.invoicenumber}</h2>
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div className="d-flex align-items-center gap-2 text-muted">
                  <FontAwesomeIcon icon={customerData?.type === 'company' ? faBuilding : faUser} size="sm" />
                  <span style={{ fontWeight: '500' }}>{customerData?.name || invoice.customername}</span>
                </div>
                <div className="d-flex align-items-center gap-2" style={{
                  padding: '0.25rem 0.75rem',
                  background: 'var(--bs-light)',
                  borderRadius: '6px',
                  fontSize: '0.8125rem'
                }}>
                  <FontAwesomeIcon icon={faCalendarAlt} size="sm" className="text-muted" />
                  <span className="text-muted">Fakturadatum:</span>
                  <span style={{ fontWeight: '600', color: 'var(--title)' }}>
                    {format(parseISO(invoice.invoicedate), 'dd MMM yyyy', { locale: sv })}
                  </span>
                </div>
                {invoice.status === INVOICE_STATUSES.PAID && paymentHistory.length > 0 ? (
                  <div className="d-flex align-items-center gap-2" style={{
                    padding: '0.25rem 0.75rem',
                    background: 'rgba(104, 227, 101, 0.1)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem'
                  }}>
                    <FontAwesomeIcon icon={faCheckCircle} size="sm" style={{ color: 'var(--bs-success)' }} />
                    <span className="text-muted">Betalning registrerad:</span>
                    <span style={{ fontWeight: '600', color: 'var(--bs-success)' }}>
                      {format(parseISO(paymentHistory[paymentHistory.length - 1].created_at || paymentHistory[paymentHistory.length - 1].payment_date), 'dd MMM yyyy', { locale: sv })}
                    </span>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-2" style={{
                    padding: '0.25rem 0.75rem',
                    background: daysUntilDue < 0 ? 'rgba(247, 43, 80, 0.1)' : daysUntilDue <= 7 ? 'rgba(255, 170, 43, 0.1)' : 'rgba(104, 227, 101, 0.1)',
                    borderRadius: '6px',
                    fontSize: '0.8125rem'
                  }}>
                    <FontAwesomeIcon
                      icon={faHourglassHalf}
                      size="sm"
                      style={{
                        color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--bs-success)'
                      }}
                    />
                    <span className="text-muted">Förfaller:</span>
                    <span style={{
                      fontWeight: '600',
                      color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--bs-success)'
                    }}>
                      {format(parseISO(invoice.deadline), 'dd MMM yyyy', { locale: sv })}
                    </span>
                    {daysUntilDue < 0 && (
                      <Badge bg="danger" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                        {Math.abs(daysUntilDue)}d försenad
                      </Badge>
                    )}
                    {daysUntilDue >= 0 && daysUntilDue <= 7 && (
                      <Badge bg="warning" text="dark" style={{ fontSize: '0.625rem', marginLeft: '0.25rem' }}>
                        {daysUntilDue}d kvar
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              {/* Periodisera indicator — shown when flag is active */}
              {invoice.use_accrual_method ? (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip>
                      Denna faktura periodiseras (fakturametoden)
                    </Tooltip>
                  }
                >
                  <div
                    className="d-flex align-items-center gap-1"
                    style={{
                      padding: '0.25rem 0.625rem',
                      borderRadius: '1rem',
                      backgroundColor: '#eff6ff',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      color: '#1e40af'
                    }}
                  >
                    <Info size={12} />
                    Periodiserad
                  </div>
                </OverlayTrigger>
              ) : null}

              <div className="action-buttons">
                <Dropdown as={ButtonGroup}>
                  {/* Primary action: always visible, greyed out when not applicable */}
                  <Button
                    className="btn btn-primary-modern btn-modern"
                    onClick={handleRegisterPayment}
                    disabled={
                      invoice.status === INVOICE_STATUSES.CANCELLED ||
                      invoice.status === INVOICE_STATUSES.DRAFT ||
                      invoice.status === INVOICE_STATUSES.PAID ||
                      invoice.status === INVOICE_STATUSES.CREDITED ||
                      (invoice.invoice_type === 'credit_note' && parseFloat(invoice.refund_amount || 0) === 0)
                    }
                  >
                    <FontAwesomeIcon icon={faCreditCard} />
                    {invoice.invoice_type === 'credit_note' && parseFloat(invoice.refund_amount || 0) > 0
                      ? `Återbetala ${formatCurrency(parseFloat(invoice.refund_amount))}`
                      : 'Registrera betalning'}
                  </Button>

                  <Dropdown.Toggle
                    split
                    className="btn btn-primary-modern btn-modern"
                    id="invoice-actions-dropdown"
                  />

                  <Dropdown.Menu align="end">
                    {/* Credit note + reminder actions — only for regular invoices in active states */}
                    {invoice.invoice_type !== 'credit_note' &&
                     ![INVOICE_STATUSES.DRAFT, INVOICE_STATUSES.CANCELLED, INVOICE_STATUSES.CREDITED].includes(invoice.status) && (
                      <>
                        <Dropdown.Item onClick={handleCreateCreditNote}>
                          <FontAwesomeIcon icon={faFileInvoice} className="me-2" />
                          Skapa kreditnota
                        </Dropdown.Item>

                        {new Date() > parseISO(invoice.deadline) && invoice.status !== INVOICE_STATUSES.PAID && (
                          <Dropdown.Item onClick={handleSendReminder}>
                            <FontAwesomeIcon icon={faBell} className="me-2" />
                            Skicka påminnelse
                          </Dropdown.Item>
                        )}

                        <Dropdown.Divider />
                      </>
                    )}

                    <Dropdown.Item
                      onClick={() => window.open(pdfData, '_blank')}
                      disabled={!pdfData}
                    >
                      <FontAwesomeIcon icon={faDownload} className="me-2" />
                      Ladda ner PDF
                    </Dropdown.Item>

                    {/* Undo action - available for Partially_Paid, Paid */}
                    {[INVOICE_STATUSES.PARTIALLY_PAID, INVOICE_STATUSES.PAID].includes(invoice.status) && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={handleUndoClick}
                          className="text-warning"
                        >
                          <FontAwesomeIcon icon={faHistory} className="me-2" />
                          Ångra senaste steg
                        </Dropdown.Item>
                      </>
                    )}

                    {/* Cancel — only for Sent invoices with no payments, not already terminal */}
                    {![INVOICE_STATUSES.CANCELLED, INVOICE_STATUSES.DRAFT, INVOICE_STATUSES.PAID, INVOICE_STATUSES.CREDITED].includes(invoice.status) && (
                      <>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={handleCancelInvoice}
                          className="text-danger"
                        >
                          <FontAwesomeIcon icon={faBan} className="me-2" />
                          Makulera faktura
                        </Dropdown.Item>
                      </>
                    )}

                  </Dropdown.Menu>
                </Dropdown>

                {/* Cogwheel settings button — periodisering toggle */}
                {(invoice.use_accrual_method || invoice.spans_fiscal_years) && (
                  <Dropdown>
                    <Dropdown.Toggle
                      variant="outline-secondary"
                      className="d-flex align-items-center justify-content-center"
                      style={{ width: '38px', height: '38px', padding: 0 }}
                      id="invoice-settings-dropdown"
                    >
                      <FontAwesomeIcon icon={faCog} />
                    </Dropdown.Toggle>
                    <Dropdown.Menu align="end" style={{ minWidth: '280px' }}>
                      {(() => {
                        const hasActivePayments = paymentHistory && paymentHistory.some(p => !p.reversed);
                        const isLocked = !['Draft', 'Sent'].includes(invoice.status) || hasActivePayments;
                        return (
                          <Dropdown.Item
                            as="div"
                            className="d-flex align-items-center justify-content-between py-2 px-3"
                            style={{ cursor: isLocked ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.5 : 1 }}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (isLocked) return;
                              try {
                                const newValue = !invoice.use_accrual_method;
                                await axios.put(
                                  getApiUrl(`${companyId}/invoices/${invoiceId}`),
                                  { use_accrual_method: newValue },
                                  { withCredentials: true }
                                );
                                toast.success(newValue ? 'Fakturan periodiseras nu (fakturametoden)' : 'Periodisering borttagen (kontantmetoden)');
                                // Reload to show updated verifications
                                setTimeout(() => window.location.reload(), 500);
                              } catch (err) {
                                console.error('Error toggling accrual method:', err);
                                const msg = err.response?.data?.error || err.response?.data?.message || 'Kunde inte ändra bokföringsmetod.';
                                toast.error(msg);
                              }
                            }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <span style={{ fontSize: '0.875rem' }}>Bokför som kundfordran</span>
                              <OverlayTrigger
                                placement="left"
                                overlay={
                                  <Tooltip>
                                    {isLocked
                                      ? 'Kan inte ändras — fakturan har betalningar eller är avslutad'
                                      : 'Bokför som kundfordran (konto 1510). Verifikation skapas på fakturadatum.'}
                                  </Tooltip>
                                }
                              >
                                <Info size={13} style={{ color: '#9ca3af', cursor: 'help' }} />
                              </OverlayTrigger>
                            </div>
                            <div style={{
                              width: '36px',
                              height: '20px',
                              borderRadius: '10px',
                              backgroundColor: invoice.use_accrual_method ? '#3b82f6' : '#d1d5db',
                              position: 'relative',
                              transition: 'background-color 0.2s ease',
                              flexShrink: 0
                            }}>
                              <div style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '50%',
                                backgroundColor: '#fff',
                                position: 'absolute',
                                top: '2px',
                                left: invoice.use_accrual_method ? '18px' : '2px',
                                transition: 'left 0.2s ease',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                              }} />
                            </div>
                          </Dropdown.Item>
                        );
                      })()}
                    </Dropdown.Menu>
                  </Dropdown>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Key Info Strip */}
        <div className="info-strip">
          <div className="info-item">
            <Banknote size={18} style={{ color: 'var(--primary)', opacity: 0.8 }} />
            <div>
              <div className="info-value" style={{ color: 'var(--primary)' }}>
                {formatCurrency(invoice.totals.grand_total)}
              </div>
              <div className="info-label">Totalt belopp</div>
            </div>
          </div>

          <div className="info-divider" />

          <div className="info-item">
            <CheckCircleLucide size={18} style={{ color: 'var(--bs-success)', opacity: 0.8 }} />
            <div>
              <div className="info-value" style={{ color: totalPaid > 0 ? 'var(--bs-success)' : 'var(--secondary)' }}>
                {formatCurrency(totalPaid)}
              </div>
              <div className="info-label">{remainingAmount > 0 ? `Kvar: ${formatCurrency(remainingAmount)}` : 'Betalt'}</div>
            </div>
          </div>

          <div className="info-divider" />

          <div className="info-item">
            {daysUntilDue < 0 ? (
              <AlertTriangleIcon size={18} style={{ color: 'var(--bs-danger)', opacity: 0.8 }} />
            ) : (
              <ClockIcon size={18} style={{ color: daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--secondary)', opacity: 0.8 }} />
            )}
            <div>
              <div className="info-value" style={{
                color: daysUntilDue < 0 ? 'var(--bs-danger)' : daysUntilDue <= 7 ? 'var(--bs-warning)' : 'var(--title)'
              }}>
                {invoice.status === 'Paid'
                  ? 'Betald'
                  : daysUntilDue < 0
                    ? `${Math.abs(daysUntilDue)}d försenad`
                    : daysUntilDue === 0
                      ? 'Förfaller idag'
                      : `${daysUntilDue}d kvar`}
              </div>
              <div className="info-label">{format(parseISO(invoice.deadline), 'dd MMM yyyy', { locale: sv })}</div>
            </div>
          </div>

          <div className="info-divider" />

          <div className="info-item">
            <UserIcon size={18} style={{ color: '#8b5cf6', opacity: 0.8 }} />
            <div>
              <div className="info-value">{customerData?.name || invoice.customername || 'Ingen kund'}</div>
              <div className="info-label">Kund</div>
            </div>
          </div>
        </div>

        {/* Main Content - Full Width Timeline */}
        <div className="main-content">
          {/* Status Stepper */}
          <div className="content-card">
            <div className="card-header">
              <h3 className="card-title">
                <FontAwesomeIcon icon={faHistory} className="card-icon" />
                Fakturahistorik
              </h3>
              <Badge bg="primary" style={{ fontSize: '0.6875rem' }}>{combinedTimeline.length} händelser</Badge>
            </div>

            {/* Status Stepper — matches supplier invoice style */}
            {(() => {
              const statusEvents = combinedTimeline.filter(e => e.type === 'status');
              const otherEvents = combinedTimeline.filter(e => e.type !== 'status');

              // Build status timestamp map from forward-only events
              const statusMap = {};
              statusEvents.forEach(event => {
                if (!event.change_type || event.change_type === 'forward') {
                  statusMap[event.status] = event.date;
                }
              });

              // Define steps based on invoice type and status progression
              // 6-state model: Draft → Sent → Partially_Paid → Paid (or Credited/Cancelled)
              const currentStatus = invoice.status;
              const steps = [
                { key: INVOICE_STATUSES.DRAFT, label: 'Utkast', icon: faClock },
                { key: INVOICE_STATUSES.SENT, label: 'Skickad', icon: faFileInvoice }
              ];

              if (currentStatus === INVOICE_STATUSES.PAID || currentStatus === INVOICE_STATUSES.PARTIALLY_PAID) {
                steps.push({
                  key: currentStatus,
                  label: currentStatus === INVOICE_STATUSES.PARTIALLY_PAID ? 'Delvis betald' : 'Betald',
                  icon: faCheckCircle
                });
              } else if (currentStatus === INVOICE_STATUSES.CANCELLED) {
                steps.push({ key: INVOICE_STATUSES.CANCELLED, label: 'Makulerad', icon: faBan });
              } else if (currentStatus === INVOICE_STATUSES.CREDITED) {
                steps.push({ key: INVOICE_STATUSES.CREDITED, label: 'Krediterad', icon: faReceipt });
              } else {
                steps.push({ key: INVOICE_STATUSES.PAID, label: 'Betald', icon: faCheckCircle });
              }

              const currentStepIndex = steps.findIndex(step => step.key === currentStatus);

              const getStepStatus = (index) => {
                if (statusMap[steps[index].key]) return 'completed';
                if (index === currentStepIndex) return 'current';
                return 'pending';
              };

              // Can undo if current step > 0 and status allows it
              const canUndo = currentStepIndex > 0 && [INVOICE_STATUSES.PARTIALLY_PAID, INVOICE_STATUSES.PAID].includes(currentStatus);

              return (
                <>
                  <div className="d-flex align-items-center justify-content-between si-steps-row">
                    {steps.map((step, index) => {
                      const stepStatus = getStepStatus(index);
                      const timestamp = statusMap[step.key];

                      return (
                        <React.Fragment key={step.key}>
                          <div className={`si-step-block ${stepStatus}`}>
                            <div className={`si-step-indicator ${stepStatus}`}>
                              {stepStatus === 'completed' ? (
                                <CheckCircleLucide size={20} />
                              ) : (
                                <FontAwesomeIcon icon={step.icon} />
                              )}
                            </div>
                            <div className="ms-3">
                              <div className="si-step-label">
                                {step.label}
                              </div>
                              {timestamp && (
                                <div className="si-step-sublabel">
                                  {format(timestamp, 'dd MMM yyyy, HH:mm', { locale: sv })}
                                </div>
                              )}
                              {index === currentStepIndex && canUndo && (
                                <button
                                  type="button"
                                  className="undo-step-link"
                                  onClick={handleUndoClick}
                                  disabled={undoLoading}
                                >
                                  <Undo2 size={12} style={{ marginRight: 3 }} />
                                  {undoLoading ? 'Ångrar...' : 'Ångra'}
                                </button>
                              )}
                              {index === currentStepIndex && currentStatus === INVOICE_STATUSES.SENT && (
                                <button
                                  type="button"
                                  className="undo-step-link"
                                  onClick={handleCreateCreditNote}
                                  style={{ color: '#f59e0b' }}
                                >
                                  <Info size={12} style={{ marginRight: 3 }} />
                                  Kreditera faktura
                                </button>
                              )}
                            </div>
                          </div>
                          {index < steps.length - 1 && (
                            <div className={`step-connector ${statusMap[steps[index].key] ? 'completed' : ''}`}></div>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </div>

                  {/* Kreditnotor — shown as info badges, not as a step in the linear flow */}
                  {invoice.credit_notes?.length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                      <div className="mb-2" style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Kreditnotor
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {invoice.credit_notes.map((cn) => (
                          <Badge
                            key={cn.id || cn.invoice_id}
                            bg="light"
                            text="dark"
                            className="d-inline-flex align-items-center gap-2 py-2 px-3"
                            style={{
                              fontSize: '0.8125rem',
                              border: '1px solid #fed7aa',
                              cursor: 'pointer',
                              background: '#fff7ed'
                            }}
                            onClick={() => history.push(`/${companyId}/edit-credit-note-new/${cn.id || cn.invoice_id}`)}
                          >
                            <FontAwesomeIcon icon={faFileInvoice} style={{ color: '#f97316' }} />
                            <span style={{ color: '#f97316', fontWeight: '600' }}>
                              {cn.creditnote_number || 'Utkast'}
                            </span>
                            <span style={{ color: '#64748b' }}>
                              {cn.total_sum ? `${Math.abs(parseFloat(cn.total_sum)).toLocaleString('sv-SE')} SEK` : ''}
                            </span>
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Kopplade Verifikationer — matches supplier invoice style */}
                  {verifications && verifications.length > 0 && (
                    <div className="mt-4 pt-3 border-top">
                      <div className="mb-2" style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Kopplade Verifikationer
                      </div>
                      <div className="d-flex gap-2 flex-wrap">
                        {verifications.map((v) => {
                          const isAnnulled = v.reference_status === 'ANNULLED';
                          const isAnnulment = v.reference_type === 'verification_reversal';

                          return (
                            <Badge
                              key={v.verification_id}
                              bg="light"
                              text="dark"
                              className="d-inline-flex align-items-center gap-2 py-2 px-3"
                              style={{
                                fontSize: '0.8125rem',
                                border: `1px solid ${isAnnulled ? '#fecaca' : isAnnulment ? '#fed7aa' : '#e2e8f0'}`,
                                transition: 'all 0.2s ease',
                                cursor: 'pointer',
                                opacity: isAnnulled ? 0.6 : 1,
                                textDecoration: isAnnulled ? 'line-through' : 'none',
                                background: isAnnulment ? '#fff7ed' : undefined
                              }}
                              onClick={() => handleViewVerification(v)}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.borderColor = isAnnulled ? '#f87171' : isAnnulment ? '#fb923c' : 'var(--primary)';
                                e.currentTarget.style.background = isAnnulled ? '#fef2f2' : isAnnulment ? '#fff7ed' : '#f0fdf4';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.borderColor = isAnnulled ? '#fecaca' : isAnnulment ? '#fed7aa' : '#e2e8f0';
                                e.currentTarget.style.background = isAnnulment ? '#fff7ed' : '#f8f9fa';
                              }}
                            >
                              <Receipt size={14} style={{ color: isAnnulled ? '#ef4444' : isAnnulment ? '#f97316' : 'var(--primary)' }} />
                              <span style={{ color: isAnnulled ? '#ef4444' : isAnnulment ? '#f97316' : 'var(--primary)', fontWeight: '600' }}>
                                {isAnnulment ? 'Annulering · ' : ''}{v.series}{v.number}
                              </span>
                              <ExternalLink size={12} style={{ color: '#64748b' }} />
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Other Events (Payments, Credit Notes) */}
                  {otherEvents.length > 0 && (
                    <div className="events-list">
                      <div className="events-list-title">Betalningar & Kreditnotor</div>
                      {otherEvents.filter(e => e.type !== 'verification').map((event, eventIndex) => (
                        <div
                          key={eventIndex}
                          className={`event-item ${event.type === 'credit_note' ? 'clickable' : ''}`}
                          onClick={
                            event.type === 'credit_note'
                              ? () => history.push(`/${companyId}/view-invoice/${event.creditNoteId}`)
                              : undefined
                          }
                        >
                          <div
                            className="event-icon"
                            style={{
                              background: event.type === 'payment'
                                ? 'rgba(104, 227, 101, 0.1)'
                                : 'rgba(255, 170, 43, 0.1)',
                              color: event.type === 'payment'
                                ? 'var(--bs-success)'
                                : 'var(--bs-warning)'
                            }}
                          >
                            <FontAwesomeIcon icon={event.icon} />
                          </div>
                          <div className="event-content">
                            <div className="event-title">
                              {event.type === 'payment' && 'Betalning registrerad'}
                              {event.type === 'credit_note' && `Kreditnota ${event.creditNoteNumber} skapad`}
                            </div>
                            <div className="event-date">
                              {format(event.date, 'dd MMM yyyy • HH:mm', { locale: sv })}
                              {event.type === 'credit_note' && (
                                <span style={{ marginLeft: '0.5rem', color:
                                  event.creditNoteStatus === INVOICE_STATUSES.DRAFT ? 'var(--bs-secondary)'
                                  : event.refundStatus === 'refunded' ? 'var(--bs-success)'
                                  : 'var(--bs-warning)'
                                }}>
                                  • {event.creditNoteStatus === INVOICE_STATUSES.DRAFT ? 'Utkast'
                                    : event.creditNoteStatus === INVOICE_STATUSES.SENT ? 'Skickad'
                                    : event.creditNoteStatus === INVOICE_STATUSES.CREDITED ? 'Krediterad'
                                    : event.refundStatus === 'refunded' ? 'Krediterad'
                                    : 'Väntar på kreditering'}
                                </span>
                              )}
                            </div>
                          </div>
                          {event.type === 'payment' && (
                            <span className="event-badge" style={{ background: 'rgba(104, 227, 101, 0.1)', color: 'var(--bs-success)' }}>
                              {formatCurrency(event.amount)}
                            </span>
                          )}
                          {event.type === 'credit_note' && (
                            <span className="event-badge" style={{ background: 'rgba(255, 170, 43, 0.1)', color: 'var(--bs-warning)' }}>
                              -{formatCurrency(event.amount)}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {combinedTimeline.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <FontAwesomeIcon icon={faHistory} size="2x" className="mb-3 opacity-50" />
                      <p style={{ fontSize: '0.875rem' }}>Ingen historik tillgänglig ännu</p>
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          {/* Invoice Notes Section */}
          <div className="content-card">
            <InvoiceNotesList invoiceId={invoiceId} />
          </div>
        </div>

        {/* Document Viewer */}
        <div className="doc-viewer-container">
          <div className="doc-toolbar">
            <div className="d-flex align-items-center gap-2">
              <File size={15} style={{ color: '#ef4444' }} />
              <span style={{ fontSize: '0.8125rem', fontWeight: '500', color: 'var(--title)' }}>
                Faktura {invoice.invoicenumber || ''}
              </span>
            </div>
            <div className="d-flex align-items-center gap-2">
              {pdfData && (
                <>
                  <Button
                    size="sm"
                    variant="light"
                    href={pdfData}
                    target="_blank"
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <Eye size={13} />
                    Öppna
                  </Button>
                  <Button
                    size="sm"
                    variant="light"
                    href={pdfData}
                    download={`Faktura-${invoice.invoicenumber || 'utkast'}.pdf`}
                    className="d-flex align-items-center gap-1"
                    style={{ fontSize: '0.75rem', borderRadius: '6px', border: '1px solid #e2e8f0' }}
                  >
                    <Download size={13} />
                  </Button>
                </>
              )}
            </div>
          </div>
          <div className="doc-frame">
            {pdfData ? (
              <Worker workerUrl="https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js">
                <Viewer fileUrl={pdfData} />
              </Worker>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', flexDirection: 'column', gap: '0.5rem' }}>
                <Spinner animation="border" size="sm" style={{ color: '#94a3b8' }} />
                <span style={{ fontSize: '0.8125rem', color: '#94a3b8' }}>Laddar dokument...</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {selectedVerification && (
        <VerificationDetailsModal
          showVerificationDetails={showVerificationDetails}
          setShowVerificationDetails={setShowVerificationDetails}
          selectedRow={selectedVerification}
          onVerificationUpdated={() => {}}
          viewOnly={true}
        />
      )}
    </>
  );
};

export default InvoiceDetailBootstrap;
